import React, { useEffect, useState, useMemo } from 'react';
import { useWebSocket, NotificationType } from '../contexts/WebSocketContext';
import { 
  X, Bell, AlertTriangle, CheckCircle, XCircle, 
  Loader2, Shield, Activity, Wifi, WifiOff, ChevronRight,
  Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Individual notification toast (for dropdown list)
const NotificationItem = ({ notification, onDismiss, expanded = false }) => {
  const { type, title, message, scan_name, progress, severity, channel, timestamp } = notification;
  
  const getIcon = () => {
    switch (type) {
      case NotificationType.SCAN_STARTED:
        return <Activity className="w-4 h-4 text-blue-400" />;
      case NotificationType.SCAN_PROGRESS:
        return <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />;
      case NotificationType.SCAN_COMPLETED:
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case NotificationType.SCAN_FAILED:
        return <XCircle className="w-4 h-4 text-red-400" />;
      case NotificationType.CRITICAL_ALERT:
        return <AlertTriangle className="w-4 h-4 text-pink-500" />;
      case NotificationType.HIGH_ALERT:
        return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case NotificationType.SECURITY_ALERT:
        return <Shield className="w-4 h-4 text-yellow-500" />;
      default:
        return <Bell className="w-4 h-4 text-slate-400" />;
    }
  };
  
  const getTitle = () => {
    switch (type) {
      case NotificationType.SCAN_STARTED:
        return `Scan Started: ${scan_name || 'Security Scan'}`;
      case NotificationType.SCAN_PROGRESS:
        return `Scanning: ${progress || 0}%`;
      case NotificationType.SCAN_COMPLETED:
        return `Scan Complete: ${scan_name || 'Security Scan'}`;
      case NotificationType.SCAN_FAILED:
        return `Scan Failed: ${scan_name || 'Security Scan'}`;
      case NotificationType.CRITICAL_ALERT:
        return title || 'Critical Security Alert';
      case NotificationType.HIGH_ALERT:
        return title || 'High Priority Alert';
      case NotificationType.SECURITY_ALERT:
        return title || 'Security Alert';
      default:
        return title || 'Notification';
    }
  };
  
  const getMessage = () => {
    switch (type) {
      case NotificationType.SCAN_PROGRESS:
        return notification.current_step || `Processing... ${progress || 0}%`;
      default:
        return message || notification.error_message || '';
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };
  
  return (
    <div className="flex items-start gap-3 p-3 hover:bg-slate-800/50 cursor-pointer transition-colors border-b border-slate-800 last:border-b-0">
      <div className="flex-shrink-0 mt-0.5">
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {getTitle()}
        </p>
        {getMessage() && (
          <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">
            {getMessage()}
          </p>
        )}
        {type === NotificationType.SCAN_PROGRESS && (
          <div className="mt-1.5 h-1 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 rounded-full transition-all duration-300"
              style={{ width: `${progress || 0}%` }}
            />
          </div>
        )}
        <p className="text-xs text-slate-500 mt-1">
          {formatTime(timestamp)}
        </p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
        className="flex-shrink-0 p-1 hover:bg-slate-700/50 rounded text-slate-500 hover:text-white transition-colors"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

// Connection status indicator
export const ConnectionStatus = ({ className = '' }) => {
  const { isConnected } = useWebSocket();
  
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {isConnected ? (
        <>
          <Wifi className="w-4 h-4 text-emerald-500" />
          <span className="text-xs text-emerald-500">Live</span>
        </>
      ) : (
        <>
          <WifiOff className="w-4 h-4 text-slate-500" />
          <span className="text-xs text-slate-500">Offline</span>
        </>
      )}
    </div>
  );
};

// Notification Summary component
const NotificationSummary = ({ notifications }) => {
  const summary = useMemo(() => {
    const counts = {
      critical: 0,
      high: 0,
      security: 0,
      scans: 0,
      other: 0
    };
    
    notifications.forEach(n => {
      switch (n.type) {
        case NotificationType.CRITICAL_ALERT:
          counts.critical++;
          break;
        case NotificationType.HIGH_ALERT:
          counts.high++;
          break;
        case NotificationType.SECURITY_ALERT:
          counts.security++;
          break;
        case NotificationType.SCAN_STARTED:
        case NotificationType.SCAN_PROGRESS:
        case NotificationType.SCAN_COMPLETED:
        case NotificationType.SCAN_FAILED:
          counts.scans++;
          break;
        default:
          counts.other++;
      }
    });
    
    return counts;
  }, [notifications]);
  
  const total = notifications.length;
  
  if (total === 0) return null;
  
  return (
    <div className="px-4 py-3 bg-slate-800/50 border-b border-slate-800">
      <p className="text-xs text-slate-400 mb-2 font-medium">Summary</p>
      <div className="grid grid-cols-2 gap-2 text-xs">
        {summary.critical > 0 && (
          <div className="flex items-center gap-2 text-pink-400">
            <AlertTriangle className="w-3 h-3" />
            <span>{summary.critical} Critical</span>
          </div>
        )}
        {summary.high > 0 && (
          <div className="flex items-center gap-2 text-orange-400">
            <AlertTriangle className="w-3 h-3" />
            <span>{summary.high} High Priority</span>
          </div>
        )}
        {summary.security > 0 && (
          <div className="flex items-center gap-2 text-yellow-400">
            <Shield className="w-3 h-3" />
            <span>{summary.security} Security</span>
          </div>
        )}
        {summary.scans > 0 && (
          <div className="flex items-center gap-2 text-blue-400">
            <Activity className="w-3 h-3" />
            <span>{summary.scans} Scans</span>
          </div>
        )}
        {summary.other > 0 && (
          <div className="flex items-center gap-2 text-slate-400">
            <Info className="w-3 h-3" />
            <span>{summary.other} Other</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Notification center component - Now shows as dropdown from bell icon, NOT auto-popup
const NotificationCenter = ({ maxVisible = 5, position = 'top-right' }) => {
  const { notifications, clearNotifications } = useWebSocket();
  const [isOpen, setIsOpen] = useState(false);
  const [visibleNotifications, setVisibleNotifications] = useState([]);
  
  // Update visible notifications when notifications change
  useEffect(() => {
    setVisibleNotifications(notifications.slice(0, maxVisible));
  }, [notifications, maxVisible]);
  
  const dismissNotification = (id) => {
    setVisibleNotifications(prev => prev.filter(n => n.id !== id));
  };

  const unreadCount = visibleNotifications.length;
  
  // Don't render anything if no notifications and dropdown is closed
  // Bell icon is always visible when there's a token (user is logged in)
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  
  // Hide on GRC pages (MainLayout has its own notification bell)
  const isGRCPage = typeof window !== 'undefined' && (window.location.pathname.startsWith('/grc') || window.location.pathname.startsWith('/apps'));
  
  if (!token || isGRCPage) {
    return null;
  }
  
  return (
    <>
      {/* Bell Button - Fixed position */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-4 right-4 z-50 p-2.5 bg-slate-800 hover:bg-slate-700 rounded-full shadow-lg transition-all border border-slate-700"
        data-testid="ws-notification-bell"
      >
        <Bell className="w-5 h-5 text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Overlay to close dropdown */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Notification Panel */}
          <div className="fixed top-16 right-4 z-50 w-96 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-800 bg-slate-900">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Notifications
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                    {unreadCount} new
                  </span>
                )}
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors"
                title="Close notifications"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Notification Summary */}
            <NotificationSummary notifications={visibleNotifications} />

            {/* Notifications List */}
            <div className="max-h-80 overflow-y-auto">
              {visibleNotifications.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No notifications</p>
                  <p className="text-xs text-slate-600 mt-1">You're all caught up!</p>
                </div>
              ) : (
                visibleNotifications.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onDismiss={() => dismissNotification(notification.id)}
                  />
                ))
              )}
            </div>

            {/* Footer */}
            {visibleNotifications.length > 0 && (
              <div className="p-3 border-t border-slate-800 bg-slate-900/50">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full text-slate-400 hover:text-white"
                  onClick={() => {
                    clearNotifications();
                    setVisibleNotifications([]);
                  }}
                >
                  Clear all notifications
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

// Scan progress banner component
export const ScanProgressBanner = ({ scanId, onComplete }) => {
  const { scanProgress, clearScanProgress } = useWebSocket();
  const progress = scanProgress[scanId];
  
  useEffect(() => {
    if (progress?.status === 'completed' && onComplete) {
      onComplete(progress);
    }
  }, [progress?.status, onComplete]);
  
  if (!progress) {
    return null;
  }
  
  const getStatusColor = () => {
    switch (progress.status) {
      case 'running':
        return 'from-blue-600 to-blue-700';
      case 'completed':
        return 'from-emerald-600 to-emerald-700';
      case 'failed':
        return 'from-red-600 to-red-700';
      default:
        return 'from-slate-600 to-slate-700';
    }
  };
  
  const getStatusText = () => {
    switch (progress.status) {
      case 'running':
        return progress.current_step || `Scanning... ${progress.progress || 0}%`;
      case 'completed':
        return `Scan complete! Found ${progress.summary?.total_findings || 0} findings`;
      case 'failed':
        return progress.error_message || 'Scan failed';
      default:
        return 'Initializing...';
    }
  };
  
  return (
    <div className={`bg-gradient-to-r ${getStatusColor()} rounded-lg p-4 mb-4`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          {progress.status === 'running' && (
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          )}
          {progress.status === 'completed' && (
            <CheckCircle className="w-5 h-5 text-white" />
          )}
          {progress.status === 'failed' && (
            <XCircle className="w-5 h-5 text-white" />
          )}
          <div>
            <p className="font-medium text-white">
              {progress.scan_name || 'Security Scan'}
            </p>
            <p className="text-sm text-white/80">
              {getStatusText()}
            </p>
          </div>
        </div>
        {progress.status !== 'running' && (
          <button
            onClick={() => clearScanProgress(scanId)}
            className="p-1 hover:bg-white/10 rounded text-white/60 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {progress.status === 'running' && (
        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white rounded-full transition-all duration-300"
            style={{ width: `${progress.progress || 0}%` }}
          />
        </div>
      )}
      
      {progress.status === 'completed' && progress.summary && (
        <div className="flex gap-4 mt-3 text-sm text-white/80">
          <span>Critical: {progress.summary.critical || 0}</span>
          <span>High: {progress.summary.high || 0}</span>
          <span>Medium: {progress.summary.medium || 0}</span>
          <span>Low: {progress.summary.low || 0}</span>
        </div>
      )}
    </div>
  );
};

// Add CSS animation
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  .animate-slideIn {
    animation: slideIn 0.3s ease-out;
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(style);
}

export default NotificationCenter;
